import { type ReactNode, useId, useState } from "react";

/**
 * Los gráficos del panel, dibujados a mano en SVG.
 *
 * Sin librería de gráficas, y es una decisión, no una pereza: Chart.js son
 * ~200 KB para dibujar cuatro polilíneas, hay que cargarlo desde algún sitio
 * —y la política de contenido de esta aplicación no deja traerlo de fuera— y
 * trae su propio sistema de colores que habría que pelear para que respete la
 * marca. Lo que hay aquí son unas decenas de líneas de trigonometría de
 * instituto que se leen enteras en un minuto.
 *
 * ── La regla que gobierna este fichero ───────────────────────────────────
 *
 * **Número primero; gráfico sólo si aporta.** Un gráfico sirve para ver una
 * tendencia, un reparto, una comparación o una relación. Si lo que hay que
 * decir es «47», se escribe 47. Un donut de dos porciones y una barra sola son
 * decoración que ocupa el sitio donde iría algo que sí decide.
 *
 * Y ninguno de estos dibuja nada cuando no hay datos: devuelven el hueco con
 * su motivo, igual que las cifras. Un gráfico vacío con ejes es peor que no
 * pintarlo — parece que se midió y salió cero.
 */

/**
 * La ayuda de una cifra: qué es y por qué importa.
 *
 * Va en TODAS, y no es adorno. El panel lo mira un cliente que no sabe qué es
 * una impresión ni por qué bajar de posición es bueno: una cifra que no se
 * entiende no se lee, se ignora — y entonces da igual lo bien medida que esté.
 *
 * Se abre al pasar el ratón y también al pulsar, porque en un móvil no hay
 * ratón que pasar. Y lleva `aria-label` para quien navegue con lector.
 */
export function Ayuda({ texto }: { texto: string }) {
  const [abierta, setAbierta] = useState(false);
  const id = useId();

  return (
    <span className="ayuda">
      <button
        type="button"
        className="ayuda-boton"
        aria-label={`Qué significa: ${texto}`}
        aria-expanded={abierta}
        aria-describedby={abierta ? id : undefined}
        onClick={() => setAbierta((a) => !a)}
        onBlur={() => setAbierta(false)}
      >
        ?
      </button>
      <span className="ayuda-globo" id={id} role="tooltip">
        {texto}
      </span>
    </span>
  );
}

/** El hueco de un gráfico, con su motivo. Nunca ejes vacíos. */
export function SinGrafico({ que }: { que: string }) {
  return <p className="grafico-vacio">{que}</p>;
}

export interface PuntoSerie {
  mes: string;
  valor: number | null;
  /** Sobre cuántas cosas se calculó. Distingue tendencia de casualidad. */
  sobre?: number;
}

/**
 * Una serie mensual, en área.
 *
 * `menosEsMejor` invierte el eje: en posición media, el puesto 1 va ARRIBA.
 * Con el eje normal, una mejora bajaría por la pantalla y el ojo lee lo
 * contrario de lo que pasó — que es el mismo error del que protege
 * `MENOS_ES_MEJOR` en los números.
 */
export function Serie({
  puntos,
  menosEsMejor,
  formato,
}: {
  puntos: PuntoSerie[];
  menosEsMejor?: boolean;
  formato?: (v: number) => string;
}) {
  const conDato = puntos.filter((p) => p.valor !== null) as (PuntoSerie & {
    valor: number;
  })[];
  if (conDato.length < 2) {
    return (
      <SinGrafico
        que={
          conDato.length === 0
            ? "Todavía no hay ningún mes medido."
            : "Sólo hay un mes medido: con uno no hay tendencia que dibujar."
        }
      />
    );
  }

  const ancho = 560;
  const alto = 170;
  const abajo = 24;
  const max = Math.max(...conDato.map((p) => p.valor));
  const min = Math.min(...conDato.map((p) => p.valor));
  const rango = max - min || 1;
  const paso = ancho / (conDato.length - 1);

  const y = (v: number) => {
    const norm = (v - min) / rango;
    // Invertido para «menos es mejor»: lo bueno arriba, siempre.
    return (menosEsMejor ? norm : 1 - norm) * (alto - abajo - 12) + 8;
  };

  const puntosSvg = conDato.map(
    (p, i) => `${(i * paso).toFixed(1)},${y(p.valor).toFixed(1)}`,
  );
  const area = `0,${alto - abajo} ${puntosSvg.join(" ")} ${ancho},${alto - abajo}`;

  return (
    <div className="grafico">
      <svg
        viewBox={`0 0 ${ancho} ${alto}`}
        preserveAspectRatio="none"
        role="img"
      >
        <polygon points={area} className="area" />
        <polyline points={puntosSvg.join(" ")} className="linea" />
        {conDato.map((p, i) => (
          <circle
            key={p.mes}
            cx={i * paso}
            cy={y(p.valor)}
            r={3}
            className="punto"
          >
            <title>
              {p.mes}: {formato ? formato(p.valor) : p.valor}
              {p.sobre !== undefined ? ` · sobre ${p.sobre} keywords` : ""}
            </title>
          </circle>
        ))}
      </svg>
      <div className="grafico-eje">
        {conDato.map((p) => (
          <span key={p.mes}>{p.mes.slice(5)}</span>
        ))}
      </div>
    </div>
  );
}

export interface Tramo {
  mes: string;
  top3: number;
  cuatroADiez: number;
  onceATreinta: number;
  masAlla: number;
  sinPosicion: number;
}

/**
 * El reparto de keywords por tramo, mes a mes, en barras apiladas.
 *
 * Los tramos no son arbitrarios: del 1 al 3 se lleva la mayoría de los clics,
 * del 4 al 10 se ve pero se pulsa poco, y del 11 en adelante casi nadie pasa.
 * Apilados se ve lo único que importa aquí — si el trabajo mueve keywords
 * hacia arriba o sólo añade más abajo, que se parecen mucho en un total.
 *
 * «Sin posición» va incluido y en gris: son keywords que se miden y no
 * aparecen. Quitarlas haría que el total subiera al empeorar.
 */
export function Tramos({ meses }: { meses: Tramo[] }) {
  if (meses.length === 0) {
    return <SinGrafico que="Todavía no hay ningún mes con keywords medidas." />;
  }

  const capas = [
    ["top3", "Del 1 al 3", "t3"],
    ["cuatroADiez", "Del 4 al 10", "t10"],
    ["onceATreinta", "Del 11 al 30", "t30"],
    ["masAlla", "Más del 30", "t99"],
    ["sinPosicion", "Sin posición", "tsin"],
  ] as const;

  const total = (m: Tramo) => capas.reduce((s, [k]) => s + m[k], 0);
  const mayor = Math.max(...meses.map(total), 1);

  return (
    <div className="grafico">
      <div className="tramos">
        {meses.map((m) => (
          <div className="tramos-col" key={m.mes}>
            <div
              className="tramos-pila"
              style={{ height: `${(total(m) / mayor) * 100}%` }}
            >
              {capas.map(([k, nombre, clase]) => {
                const v = m[k];
                if (v === 0) return null;
                return (
                  <div
                    key={k}
                    className={`tramo ${clase}`}
                    style={{ flexGrow: v }}
                    title={`${m.mes} · ${nombre}: ${v} keyword${v === 1 ? "" : "s"}`}
                  />
                );
              })}
            </div>
            <span className="tramos-mes">{m.mes.slice(5)}</span>
          </div>
        ))}
      </div>
      <div className="leyenda">
        {capas.map(([k, nombre, clase]) => (
          <span key={k}>
            <i className={clase} />
            {nombre}
          </span>
        ))}
      </div>
    </div>
  );
}

export interface Empujon {
  consulta: string;
  posicion: number;
  impresiones: number;
  clics: number;
  objetivo: number;
  probabilidad: number;
  siLoConsigue: number;
  valor: number;
}

/**
 * Las consultas a un empujón, en burbujas.
 *
 * Dos ejes y un tamaño, y cada uno dice algo distinto: la posición (qué lejos
 * está), las impresiones (cuánta gente lo busca) y el tamaño (cuántos clics se
 * ganarían). Una tabla con esas tres columnas obliga a comparar de memoria;
 * aquí se ve de un vistazo cuál está cerca Y es grande.
 *
 * El eje de posición va INVERTIDO —la 1 a la derecha— para que «mejor» sea
 * hacia donde se avanza al leer.
 */
export function Burbujas({ empujones }: { empujones: Empujon[] }) {
  if (empujones.length === 0) {
    return (
      <SinGrafico que="Ninguna consulta está en la ventana del empujón ahora mismo." />
    );
  }

  const ancho = 560;
  const alto = 200;
  const margen = { i: 8, d: 30, arriba: 14, abajo: 26 };
  const maxImpr = Math.max(...empujones.map((e) => e.impresiones));
  const maxValor = Math.max(...empujones.map((e) => e.valor), 1);

  // De la posición 20 a la 3, de izquierda a derecha: avanzar es mejorar.
  const x = (pos: number) =>
    margen.i +
    ((20 - Math.min(20, Math.max(3, pos))) / 17) *
      (ancho - margen.i - margen.d);
  const y = (impr: number) =>
    alto -
    margen.abajo -
    (impr / maxImpr) * (alto - margen.arriba - margen.abajo);
  const r = (valor: number) => 5 + Math.sqrt(valor / maxValor) * 16;

  return (
    <div className="grafico">
      <svg viewBox={`0 0 ${ancho} ${alto}`} role="img" className="burbujas">
        <line
          x1={margen.i}
          y1={alto - margen.abajo}
          x2={ancho}
          y2={alto - margen.abajo}
        />
        {empujones.map((e) => (
          <circle
            key={e.consulta}
            cx={x(e.posicion)}
            cy={y(e.impresiones)}
            r={r(e.valor)}
          >
            <title>
              {e.consulta} · posición {e.posicion} ·{" "}
              {e.impresiones.toLocaleString("es-ES")} impresiones → ganaría{" "}
              {e.siLoConsigue} clics si sube al puesto {e.objetivo}
            </title>
          </circle>
        ))}
      </svg>
      <div className="grafico-eje entre">
        <span>puesto 20</span>
        <span>impresiones ↑ · mejor posición →</span>
        <span>puesto 3</span>
      </div>
    </div>
  );
}

export interface Barra {
  etiqueta: string;
  valor: number;
  detalle?: string;
}

/**
 * Barras horizontales para un reparto con nombres largos.
 *
 * Horizontal y no vertical porque las etiquetas son frases —«Rastreo e
 * indexación», «Meta etiquetas»— y en vertical habría que girarlas, que es la
 * forma más rápida de que nadie las lea.
 */
export function BarrasH({
  barras,
  sufijo,
}: {
  barras: Barra[];
  sufijo?: string;
}) {
  if (barras.length === 0)
    return <SinGrafico que="No hay nada que repartir todavía." />;
  const mayor = Math.max(...barras.map((b) => b.valor), 1);

  return (
    <div className="barras-h">
      {barras.map((b) => (
        <div className="barra-fila" key={b.etiqueta}>
          <span className="barra-et" title={b.etiqueta}>
            {b.etiqueta}
          </span>
          <span className="barra-via">
            <span
              className="barra-relleno"
              style={{ width: `${(b.valor / mayor) * 100}%` }}
            />
          </span>
          <span className="barra-val">
            {b.valor.toLocaleString("es-ES")}
            {sufijo ?? ""}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Una caja del panel, con su título, su fuente y su ayuda. */
export function Caja({
  titulo,
  fuente,
  ayuda,
  pie,
  children,
}: {
  titulo: string;
  fuente?: string;
  ayuda: string;
  pie?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="caja-seo">
      <header>
        <h3>
          {titulo}
          <Ayuda texto={ayuda} />
        </h3>
        {fuente && <span className="pill">{fuente}</span>}
      </header>
      {children}
      {pie}
    </section>
  );
}

/**
 * Qué es cada cifra, explicado para quien no sabe de SEO.
 *
 * Va en la pantalla y no en el servidor a propósito: el `alcance` que viaja con
 * el dato dice **qué abarca** —«de búsqueda orgánica, del mes del informe»— y
 * eso es parte del dato. Esto otro dice **qué significa y por qué importa**, que
 * es una decisión de cómo se presenta y cambia según a quién se le enseñe.
 *
 * Lo que no puede pasar es que una cifra se quede sin ayuda por olvido: quien
 * pinta cae al `alcance` cuando aquí no hay entrada, así que el peor caso es
 * una ayuda escueta, nunca ninguna.
 */
export const AYUDAS: Record<string, string> = {
  enPrimeraPagina:
    "Cuántas de las búsquedas por las que quieres que te encuentren están entre los diez primeros resultados de Google. Es donde ocurre casi todo: a la segunda página llega muy poca gente.",
  clics:
    "Veces que alguien pulsó tu web en los resultados de Google. No son visitas totales: son las que llegaron buscando, sin pagar por ellas.",
  impresiones:
    "Veces que tu web apareció en los resultados, la pulsaran o no. Sirve para saber si el problema es que no te ven o que te ven y no te pulsan.",
  posicionMedia:
    "En qué puesto sales de media en Google. Cuanto más bajo el número, mejor: 1 es el primer resultado. Por eso bajar aquí es mejorar.",
  visitas:
    "Personas que entraron en tu web desde una búsqueda. No incluye a quien llegó escribiendo tu dirección o desde redes: sólo lo que trae el SEO.",
  usuarios:
    "Personas distintas, no visitas. Si alguien entra tres veces en el mes, cuenta una vez aquí y tres en visitas.",
  contactos:
    "Formularios enviados y llamadas pulsadas por gente que llegó buscando. Es la cifra que más se parece a dinero.",
  keywordsTop3:
    "Búsquedas donde sales entre los tres primeros resultados. Es donde se lleva la mayoría de los clics: del cuarto puesto en adelante la caída es muy brusca.",
  salud:
    "Una nota de 0 a 100 que resume la auditoría técnica: pesa más un error —que bloquea a Google— que un aviso, y se calcula sobre las páginas rastreadas. Verde: nada urgente. Ámbar: hay que mirarlo. Rojo: hoy.",
  errores:
    "Fallos técnicos que la última auditoría encontró en la web: enlaces rotos, páginas que Google no puede leer, etiquetas duplicadas. No todos urgen igual.",
  informesConReparos:
    "Informes que entraron con alguna pega de calidad de dato: un bloque que faltaba, un dominio que no cuadraba. Es control interno nuestro, no un problema de la web.",
  nota: "La nota de velocidad que le pone Google a la web en móvil, de 0 a 100. Por debajo de 50 la gente se va antes de que cargue.",
  backlinks:
    "Enlaces que otras webs hacen hacia la tuya. Google los lee como recomendaciones: cuantas más webs distintas te enlacen, más te cree.",
  dominiosDeReferencia:
    "Cuántas webs DISTINTAS te enlazan. Vale más que el total de enlaces: cien enlaces de una sola web es una web, no cien recomendaciones.",
  domainTrust:
    "La nota de confianza que SE Ranking le da a tu dominio, de 0 a 100. Sube despacio y con enlaces de sitios serios.",
  noIndexables:
    "Páginas que Google no puede guardar en su índice, así que no pueden salir en ninguna búsqueda. A veces es a propósito y a veces es un fallo.",
  tareasSinEncargar:
    "Cosas detectadas que todavía no tiene nadie asignadas. Salen ordenadas por lo que devuelven, no por lo grandes que suenan.",
  tareasEncargadas:
    "Tareas que ya están en Trello con alguien detrás y aún sin cerrar.",
  enJuego:
    "Clics al mes que se ganarían si se hicieran las tareas que están sin encargar. Es una estimación, no una promesa.",
  objetivos:
    "Metas acordadas con el cliente, con su número de partida y su fecha.",
  objetivosSinPartida:
    "Objetivos apuntados sin anotar de dónde se salía. Dentro de tres meses no habrá forma de decir si se cumplieron.",
  descubrimiento:
    "Gente que buscaba lo que haces —«dentista en Bilbao»— y encontró tu ficha sin conocerte de nada. Es la que dice si el SEO local funciona.",
  directas:
    "Gente que ya te buscaba a ti por tu nombre. Sube cuando te conocen por otra vía, no tanto por el SEO.",
  llamadas:
    "Cuántas veces alguien pulsó el botón de llamar en tu ficha de Google. Google no dice quién llamó ni desde qué número, sólo cuántos.",
  comoLlegar:
    "Veces que alguien pidió la ruta hasta tu puerta desde Google Maps.",
  clicsALaWeb:
    "Visitas que llegaron a tu web desde la ficha de Google o desde Maps.",
};

export interface Linea {
  nombre: string;
  puntos: { mes: string; valor: number | null; aMedias?: boolean }[];
  /** Apagada: contexto, no protagonista. */
  tenue?: boolean;
}

/**
 * Dos o más series mensuales en el mismo gráfico, con una destacada.
 *
 * En un panel de SEO el protagonista es el orgánico y el total es contexto: el
 * total sube porque alguien hizo una campaña o mandó una newsletter, y
 * pintarlos igual hace que el SEO parezca responsable de todo lo que pasa en la
 * web. Por eso hay una línea viva y las demás apagadas — no es decoración, es
 * quién responde de qué.
 *
 * El mes en curso se dibuja PUNTEADO. Va incluido porque esconderlo tapa lo que
 * está pasando ahora, pero sin marcar dibuja una caída a plomo cada día 1: son
 * cinco días contra los treinta del mes anterior.
 */
export function SerieDoble({
  lineas,
  formato,
}: {
  lineas: Linea[];
  formato?: (v: number) => string;
}) {
  const meses = [
    ...new Set(lineas.flatMap((l) => l.puntos.map((p) => p.mes))),
  ].sort();
  if (meses.length < 2) {
    return (
      <SinGrafico
        que={
          meses.length === 0
            ? "Todavía no hay ningún mes medido."
            : "Sólo hay un mes medido: con uno no hay tendencia que dibujar."
        }
      />
    );
  }

  const ancho = 560;
  const alto = 180;
  const abajo = 22;
  const todos = lineas
    .flatMap((l) => l.puntos.map((p) => p.valor))
    .filter((v): v is number => v !== null);
  const max = Math.max(...todos, 1);
  const paso = ancho / (meses.length - 1);
  const y = (v: number) => alto - abajo - (v / max) * (alto - abajo - 10);

  return (
    <div className="grafico">
      <svg
        viewBox={`0 0 ${ancho} ${alto}`}
        preserveAspectRatio="none"
        role="img"
      >
        {lineas.map((l) => {
          const pts = meses
            .map((m, i) => {
              const p = l.puntos.find((x) => x.mes === m);
              return p && p.valor !== null
                ? { x: i * paso, y: y(p.valor), aMedias: p.aMedias }
                : null;
            })
            .filter((p) => p !== null);
          if (pts.length < 2) return null;

          // El tramo del mes en curso va aparte y punteado.
          const firmes = pts.filter((p) => !p.aMedias);
          const enCurso = pts.slice(Math.max(0, firmes.length - 1));

          return (
            <g
              key={l.nombre}
              className={l.tenue ? "linea-tenue" : "linea-viva"}
            >
              {!l.tenue && firmes.length > 1 && (
                <polygon
                  className="area"
                  points={`0,${alto - abajo} ${firmes.map((p) => `${p.x},${p.y}`).join(" ")} ${firmes.at(-1)!.x},${alto - abajo}`}
                />
              )}
              <polyline
                className="linea"
                points={firmes.map((p) => `${p.x},${p.y}`).join(" ")}
              />
              {enCurso.length > 1 && (
                <polyline
                  className="linea a-medias"
                  points={enCurso.map((p) => `${p.x},${p.y}`).join(" ")}
                />
              )}
            </g>
          );
        })}
        {/* Los puntos de la línea viva, con su valor al pasar por encima. */}
        {lineas
          .filter((l) => !l.tenue)
          .flatMap((l) =>
            meses.map((m, i) => {
              const p = l.puntos.find((x) => x.mes === m);
              if (!p || p.valor === null) return null;
              return (
                <circle
                  key={`${l.nombre}-${m}`}
                  cx={i * paso}
                  cy={y(p.valor)}
                  r={3}
                  className="punto"
                >
                  <title>
                    {m}:{" "}
                    {formato
                      ? formato(p.valor)
                      : p.valor.toLocaleString("es-ES")}{" "}
                    {l.nombre}
                    {p.aMedias ? " · mes sin cerrar" : ""}
                  </title>
                </circle>
              );
            }),
          )}
      </svg>
      <div className="grafico-eje">
        {meses.map((m) => (
          <span key={m}>{m.slice(5)}</span>
        ))}
      </div>
      <div className="leyenda">
        {lineas.map((l) => (
          <span key={l.nombre}>
            <i className={l.tenue ? "l-tenue" : "l-viva"} />
            {l.nombre}
          </span>
        ))}
        <span>
          <i className="l-punteada" />
          mes sin cerrar
        </span>
      </div>
    </div>
  );
}
